import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text, Localizer } from 'preact-i18n';

import MarkdownDisplay from '../gui/markdownDisplay';
import localUtil from '../../lib/util';
import {
    ASSIST_TOOLS, TONE_OPTIONS, assistResultButtons, buildAssistPayload, isRewriteAction,
} from '../../lib/articleAssist';
import { getTextareaSelection, findBodyTextarea } from '../../lib/editorSelection';

const RESULT_BUTTONS = {
    replace: { textId: 'article.aiReplace', text: 'Replace text', cls: 'btn-primary', icon: 'fa-solid fa-arrows-rotate' },
    append: { textId: 'article.aiAppend', text: 'Append at end', cls: 'btn-success', icon: 'fa-solid fa-arrow-down-to-line' },
    useTitle: { textId: 'article.aiUseTitle', text: 'Use title', cls: 'btn-primary', icon: 'fa-solid fa-heading' },
    useIngress: { textId: 'article.aiUseIngress', text: 'Use intro', cls: 'btn-primary', icon: 'fa-solid fa-align-left' },
    discard: { textId: 'article.aiDiscard', text: 'Discard', cls: 'btn-outline-secondary', icon: 'fa-solid fa-xmark' },
};

/**
 * Toolbar with AI writing helpers for the article body, plus the result panel
 * where the writer decides what to do with the suggestion. All actions run on
 * the whole body; nothing is written into the article until a button is pressed.
 */
@observer
class ArticleBodyAiTools extends Component {
    constructor(props) {
        super(props);
        this.state = {
            showTones: false,
            instruction: '',
            needsBody: false,
            selectedText: '',
        };
    }

    componentDidMount() {
        document.addEventListener('selectionchange', this.onSelectionChange);
    }

    componentWillUnmount() {
        document.removeEventListener('selectionchange', this.onSelectionChange);
        const { articleStore } = this.props.stores;
        articleStore.clearAiAssist();
        articleStore.clearAiResearch();
    }

    // Remember what is marked in the body editor. Clicking a toolbar button
    // blurs the textarea, so the selection is captured while it is focused.
    onSelectionChange = () => {
        const textarea = findBodyTextarea(this.base);
        if (!textarea || document.activeElement !== textarea) return;
        const selectedText = getTextareaSelection(textarea);
        if (selectedText !== this.state.selectedText) {
            this.setState({ selectedText });
        }
    }

    research = async () => {
        const { stores, objectName } = this.props;
        const { articleStore } = stores;
        const { selectedText } = this.state;
        if (!selectedText) return;
        const article = articleStore[objectName] || {};
        await articleStore.researchAi({ text: selectedText, title: article.title || '' });
    }

    researchSourcesMarkdown(result) {
        const sources = result.sources || [];
        if (!sources.length) return '';
        return `\n\nKilder:\n${sources.map(src => `- [${src.title || src.url}](${src.url})`).join('\n')}`;
    }

    applyResearch = async (e) => {
        const { how } = e.currentTarget.dataset;
        const { stores, objectName } = this.props;
        const { articleStore } = stores;
        const { result } = articleStore.aiResearch;
        if (how === 'discard' || !result) {
            articleStore.clearAiResearch();
            return;
        }
        const markdown = `${result.text || ''}${this.researchSourcesMarkdown(result)}`;
        if (how === 'append') {
            const article = articleStore[objectName] || {};
            const current = (article.body || '').replace(/\s+$/, '');
            await articleStore.applyAssistField(objectName, 'body', `${current}\n\n${markdown}`);
            articleStore.clearAiResearch();
        } else if (how === 'copy' && navigator.clipboard) {
            await navigator.clipboard.writeText(markdown);
        }
    }

    renderResearchResult() {
        const { articleStore } = this.props.stores;
        const { result, text } = articleStore.aiResearch;
        if (!result) return null;
        const sources = result.sources || [];
        return (
            <div class='card mt-2'>
                <div class='card-body p-2'>
                    <div class='text-body-secondary small mb-1'>
                        <i class='fa-solid fa-magnifying-glass me-1' />
                        <Text id='article.aiResearchResult'>Research</Text>: <span class='fst-italic'>«{text}»</span>
                    </div>
                    <MarkdownDisplay markdown={result.text || ''} markedOpts={localUtil.getMarkdownOptions()} stores={this.props.stores} />
                    {sources.length > 0 && (
                        <div class='mt-2 small'>
                            <div class='text-body-secondary'><Text id='article.aiSources'>Sources</Text></div>
                            <ol class='mb-0 ps-3'>
                                {sources.map(src => (
                                    <li key={src.url}>
                                        <a href={src.url} target='_blank' rel='noopener noreferrer'>{src.title || src.url}</a>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    )}
                    <div class='d-flex flex-wrap justify-content-end gap-1 mt-2'>
                        <button type='button' class='btn btn-sm btn-success rounded-pill' data-how='append' onClick={this.applyResearch}>
                            <i class='fa-solid fa-arrow-down-to-line me-1' />
                            <Text id='article.aiAppend'>Append at end</Text>
                        </button>
                        <button type='button' class='btn btn-sm btn-outline-secondary rounded-pill' data-how='copy' onClick={this.applyResearch}>
                            <i class='fa-solid fa-copy me-1' />
                            <Text id='article.aiCopy'>Copy</Text>
                        </button>
                        <button type='button' class='btn btn-sm btn-outline-secondary rounded-pill' data-how='discard' onClick={this.applyResearch}>
                            <i class='fa-solid fa-xmark me-1' />
                            <Text id='article.aiDiscard'>Discard</Text>
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    run = async (action, extra = {}) => {
        const { stores, objectName } = this.props;
        const { articleStore } = stores;
        const article = articleStore[objectName] || {};
        if (!article.body?.trim()) {
            this.setState({ needsBody: true });
            return;
        }
        this.setState({ needsBody: false, showTones: false });
        await articleStore.assistAi(buildAssistPayload(article, { action, ...extra }));
    }

    onToolClick = (e) => {
        const { action } = e.currentTarget.dataset;
        if (action === 'tone') {
            this.setState(prev => ({ showTones: !prev.showTones }));
            return;
        }
        this.run(action);
    }

    onToneClick = (e) => {
        const { tone } = e.currentTarget.dataset;
        this.run('tone', { tone });
    }

    onInstructionInput = (e) => {
        this.setState({ instruction: e.target.value });
    }

    onInstructionKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            this.runCustom();
        }
    }

    runCustom = () => {
        const instruction = this.state.instruction.trim();
        if (!instruction) return;
        this.run('custom', { instruction });
    }

    apply = (e) => {
        const { how } = e.currentTarget.dataset;
        return this.applyHow(how);
    }

    // Also used by the preview's change view (via edit.js) with the same semantics.
    applyHow = async (how) => {
        const { stores, objectName } = this.props;
        const { articleStore } = stores;
        const { result } = articleStore.aiAssist;
        const article = articleStore[objectName] || {};
        if (how === 'discard' || !result) {
            articleStore.clearAiAssist();
            return;
        }
        if (how === 'replace') {
            await articleStore.applyAssistField(objectName, 'body', result.text || '');
        } else if (how === 'append') {
            const current = (article.body || '').replace(/\s+$/, '');
            await articleStore.applyAssistField(objectName, 'body', `${current}\n\n${result.text || ''}`);
        } else if (how === 'useTitle' && result.title) {
            await articleStore.applyAssistField(objectName, 'title', result.title);
        } else if (how === 'useIngress' && result.ingress) {
            await articleStore.applyAssistField(objectName, 'ingress', result.ingress);
        }
        if (how === 'replace' || how === 'append') {
            articleStore.clearAiAssist();
        }
    }

    renderResult() {
        const { articleStore } = this.props.stores;
        const { action, result } = articleStore.aiAssist;
        if (!result) return null;
        const buttons = assistResultButtons(action);
        return (
            <div class='card mt-2'>
                <div class='card-body p-2'>
                    <div class='text-body-secondary small mb-1'>
                        <i class='fa-solid fa-message-bot me-1' />
                        <Text id='article.aiSuggestion'>AI suggestion</Text>
                    </div>
                    {action === 'titleIngress' && (result.title || result.ingress) ? (
                        <div class='d-flex flex-column gap-1'>
                            {result.title && <div class='fw-bold'>{result.title}</div>}
                            {result.ingress && <div>{result.ingress}</div>}
                        </div>
                    ) : isRewriteAction(action) ? (
                        <div class='text-body-secondary'>
                            <i class='fa-solid fa-arrow-right me-1' />
                            <Text id='article.aiSeeChangesInPreview'>See the changes marked in the preview.</Text>
                        </div>
                    ) : (
                        <MarkdownDisplay markdown={result.text || ''} markedOpts={localUtil.getMarkdownOptions()} stores={this.props.stores} />
                    )}
                    <div class='d-flex flex-wrap justify-content-end gap-1 mt-2'>
                        {buttons.map((how) => {
                            const btn = RESULT_BUTTONS[how];
                            return (
                                <button
                                    key={how}
                                    type='button'
                                    class={`btn btn-sm ${btn.cls} rounded-pill`}
                                    data-how={how}
                                    onClick={this.apply}
                                >
                                    <i class={`${btn.icon} me-1`} />
                                    <Text id={btn.textId}>{btn.text}</Text>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const { articleStore } = this.props.stores;
        const { showTones, instruction, needsBody, selectedText } = this.state;
        const { loading, action: runningAction, error } = articleStore.aiAssist;
        const { loading: researchLoading, error: researchError } = articleStore.aiResearch;

        return (
            <div class='article-body-ai-tools w-100'>
                <div class='d-flex flex-wrap align-items-center gap-1'>
                    <span class='text-body-secondary small me-1'>
                        <i class='fa-solid fa-message-bot me-1' />
                        <Text id='article.aiTools'>AI tools</Text>
                    </span>
                    {ASSIST_TOOLS.map(tool => (
                        <button
                            key={tool.action}
                            type='button'
                            class={`btn btn-sm rounded-pill ${showTones && tool.action === 'tone' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                            data-action={tool.action}
                            onClick={this.onToolClick}
                            disabled={loading}
                        >
                            <i class={`${loading && runningAction === tool.action ? 'fa-solid fa-spinner fa-spin' : tool.icon} me-1`} />
                            <Text id={tool.textId}>{tool.text}</Text>
                        </button>
                    ))}
                    <button
                        type='button'
                        class={`btn btn-sm rounded-pill ${selectedText ? 'btn-outline-info' : 'btn-outline-secondary'}`}
                        onClick={this.research}
                        disabled={loading || researchLoading || !selectedText}
                        title={selectedText ? `«${selectedText.slice(0, 80)}»` : ''}
                    >
                        <i class={`${researchLoading ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-magnifying-glass'} me-1`} />
                        <Text id='article.aiResearch'>Research this</Text>
                    </button>
                    {!selectedText && !researchLoading && (
                        <span class='text-body-tertiary small'>
                            <Text id='article.aiResearchHint'>Mark text in the editor to research it.</Text>
                        </span>
                    )}
                </div>
                {showTones && (
                    <div class='d-flex flex-wrap align-items-center gap-1 mt-1 ms-3'>
                        {TONE_OPTIONS.map(tone => (
                            <button
                                key={tone.value}
                                type='button'
                                class='btn btn-sm btn-outline-secondary rounded-pill'
                                data-tone={tone.value}
                                onClick={this.onToneClick}
                                disabled={loading}
                            >
                                <Text id={tone.textId}>{tone.text}</Text>
                            </button>
                        ))}
                    </div>
                )}
                <div class='input-group input-group-sm mt-1'>
                    <Localizer>
                        <input
                            type='text'
                            class='form-control'
                            value={instruction}
                            onInput={this.onInstructionInput}
                            onKeyDown={this.onInstructionKeyDown}
                            placeholder={<Text id='article.aiCustomPlaceholder'>Ask the AI for something, e.g. "Add a call to action at the end"</Text>}
                            disabled={loading}
                        />
                    </Localizer>
                    <button type='button' class='btn btn-outline-secondary' onClick={this.runCustom} disabled={loading || !instruction.trim()}>
                        <i class={`${loading && runningAction === 'custom' ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-paper-plane'} me-1`} />
                        <Text id='article.aiCustomRun'>Run</Text>
                    </button>
                </div>
                {loading && (
                    <div class='text-body-secondary small mt-1'>
                        <i class='fa-solid fa-spinner fa-spin me-1' />
                        <Text id='article.aiThinking'>AI is working …</Text>
                    </div>
                )}
                {needsBody && (
                    <div class='text-warning small mt-1'>
                        <Text id='article.aiNeedsBody'>Write some text first.</Text>
                    </div>
                )}
                {error && (
                    <div class='text-danger small mt-1'>
                        <Text id='article.aiError'>The AI request failed. Please try again.</Text>
                    </div>
                )}
                {researchError && (
                    <div class='text-danger small mt-1'>
                        <Text id='article.aiError'>The AI request failed. Please try again.</Text>
                    </div>
                )}
                {this.renderResult()}
                {this.renderResearchResult()}
            </div>
        );
    }
}

export default ArticleBodyAiTools;
